import React from 'react';
import { QRCodeSVG } from 'qrcode.react';

/*
  LabelPreview — vista previa estática de la etiqueta (miniaturas, revisión en lote).

  Cuando item.layout está presente, renderiza con posiciones absolutas (editor drag).
  Si no, usa el auto-layout según la altura configurada.

  Props:
    item   { qrValue, productName, variationLabel, price, sku, loteNumero,
             storeName, qrScale, priceSize, nameSize, varSize, layout? }
    config { width, height } mm
*/

const _canvas = typeof document !== 'undefined' ? document.createElement('canvas') : null;
const _ctx    = _canvas ? _canvas.getContext('2d') : null;
const PT_MM   = 0.353;
const LH      = 0.424;

function lhMm(pt) { return pt * LH; }

const QR_MIN = 0.45, QR_MAX = 1;
function clampQr(v) {
  const n = (typeof v === 'number' && isFinite(v)) ? v : 1;
  return Math.min(QR_MAX, Math.max(QR_MIN, n));
}

function fitPt(text, weight, startPt, minPt, maxWidthMm, scale) {
  if (!text || !_ctx) return startPt;
  const maxPx = maxWidthMm * scale;
  let pt = startPt;
  while (pt > minPt) {
    _ctx.font = `${weight} ${pt * PT_MM * scale}px Helvetica, Arial, sans-serif`;
    if (_ctx.measureText(text).width <= maxPx) break;
    pt -= 0.5;
  }
  return pt;
}

export default function LabelPreview({ item = {}, config, maxWidthPx = 340, maxHeightPx = 180 }) {
  const width  = Math.max(5, parseFloat(config?.width)  || 40);
  const height = Math.max(5, parseFloat(config?.height) || 12);
  const scale  = Math.min(maxWidthPx / width, maxHeightPx / height);

  const px   = (mm) => mm * scale;
  const ptPx = (pt) => pt * PT_MM * scale;

  const qrValue = item.qrValue || 'SH-VAR-12345';
  const price   = (item.price || '').toString().trim();
  const name    = (item.productName || '').trim();
  const varia   = (item.variationLabel || '').trim();
  const code    = [item.sku, item.loteNumero].filter(Boolean).join(' · ');

  const box = {
    width: Math.round(px(width)), height: Math.round(px(height)),
    border: '1.5px solid #cbd5e1', borderRadius: '3px',
    background: '#fff', position: 'relative',
    overflow: 'hidden', flexShrink: 0,
    boxShadow: '0 1px 4px rgba(0,0,0,0.12)',
  };

  // ── LAYOUT ABSOLUTO (editor drag) ─────────────────────────────────────────
  const layout = item.layout;
  if (layout && typeof layout === 'object' && Object.keys(layout).length > 0) {
    return (
      <div style={box}>
        {layout.qr && (
          <div style={{ position: 'absolute', left: px(layout.qr.x), top: px(layout.qr.y), pointerEvents: 'none' }}>
            <QRCodeSVG value={qrValue} size={Math.max(4, Math.round(px(layout.qr.size)))} level="M" includeMargin={false} />
          </div>
        )}
        {layout.price && price && (
          <div style={{ position: 'absolute', left: px(layout.price.x), top: px(layout.price.y), fontWeight: 700, fontSize: ptPx(layout.price.fontPt), lineHeight: px(lhMm(layout.price.fontPt)) + 'px', whiteSpace: 'nowrap', overflow: 'hidden', color: '#000' }}>
            {price}
          </div>
        )}
        {layout.name && name && (
          <div style={{ position: 'absolute', left: px(layout.name.x), top: px(layout.name.y), fontWeight: 400, fontSize: ptPx(layout.name.fontPt), lineHeight: px(lhMm(layout.name.fontPt)) + 'px', whiteSpace: 'nowrap', overflow: 'hidden', color: '#111' }}>
            {name}
          </div>
        )}
        {layout.variation && varia && (
          <div style={{ position: 'absolute', left: px(layout.variation.x), top: px(layout.variation.y), fontWeight: 400, fontSize: ptPx(layout.variation.fontPt), lineHeight: px(lhMm(layout.variation.fontPt)) + 'px', whiteSpace: 'nowrap', overflow: 'hidden', color: '#555' }}>
            {varia}
          </div>
        )}
      </div>
    );
  }

  // ── AUTO-LAYOUT MÍNIMO (height < 15mm) ────────────────────────────────────
  if (height < 15) {
    const SIDE = 1, TB = 1, GAP = 1.5, LGAP = 0.5;
    const qrMm = (height * 0.95) * clampQr(item.qrScale);
    const txMm = SIDE + qrMm + GAP;
    const twMm = width - txMm - SIDE;
    const qrTop = Math.max(0, (px(height) - px(qrMm)) / 2);

    const prStart = typeof item.priceSize === 'number' ? item.priceSize : 9;
    const nStart  = typeof item.nameSize  === 'number' ? item.nameSize  : 4;
    const vStart  = typeof item.varSize   === 'number' ? item.varSize   : 3.5;
    const prPt = price ? fitPt(price, '700', prStart, 6.5, twMm, scale) : 0;
    const nPt  = name  ? fitPt(name,  '400', nStart,  2.8, twMm, scale) : 0; // Typography A: normal
    const vPt  = varia ? fitPt(varia, '400', vStart,  2.5, twMm, scale) : 0;
    const cPt  = 2.5;

    const usableH = height - 2 * TB;
    let totalH = 0;
    const lines = [];
    if (price) { lines.push('price'); totalH += lhMm(prPt); }
    if (name)  { if (lines.length) totalH += LGAP; lines.push('name'); totalH += lhMm(nPt); }
    if (varia) { if (lines.length) totalH += LGAP; lines.push('var');  totalH += lhMm(vPt); }
    let showCode = false;
    if (code && totalH + LGAP + lhMm(cPt) <= usableH) { showCode = true; }

    const top  = px(TB) + Math.max(0, (px(usableH) - px(totalH)) / 2);
    let first  = true;
    const mt   = () => { const v = first ? 0 : px(LGAP); first = false; return v; };

    return (
      <div style={box}>
        <div style={{ position: 'absolute', left: px(SIDE), top: qrTop }}>
          <QRCodeSVG value={qrValue} size={Math.max(6, Math.round(px(qrMm)))} level="M" includeMargin={false} />
        </div>
        {twMm > 4 && (
          <div style={{ position: 'absolute', left: px(txMm), top, width: px(twMm), overflow: 'hidden' }}>
            {price && <div style={{ fontWeight: 700, fontSize: ptPx(prPt), lineHeight: px(lhMm(prPt))+'px', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', color:'#000', marginTop: mt() }}>{price}</div>}
            {name  && <div style={{ fontWeight: 400, fontSize: ptPx(nPt),  lineHeight: px(lhMm(nPt)) +'px', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', color:'#111', marginTop: mt() }}>{name}</div>}
            {varia && <div style={{ fontWeight: 400, fontSize: ptPx(vPt),  lineHeight: px(lhMm(vPt)) +'px', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', color:'#555', marginTop: mt() }}>{varia}</div>}
            {showCode && <div style={{ fontWeight: 400, fontSize: ptPx(cPt), lineHeight: px(lhMm(cPt))+'px', whiteSpace:'nowrap', color:'#999', marginTop: mt() }}>{code}</div>}
          </div>
        )}
      </div>
    );
  }

  // ── AUTO-LAYOUT HORIZONTAL (15–25mm) ──────────────────────────────────────
  if (height <= 25) {
    const SIDE = 1, TB = 1.5, GAP = 1.5;
    const usableH = height - 2 * TB;
    const qrMm   = usableH * clampQr(item.qrScale);
    const txMm   = SIDE + qrMm + GAP;
    const twMm   = width - txMm - SIDE;
    const qrTop  = px(TB) + Math.max(0, (px(usableH) - px(qrMm)) / 2);

    const nStart  = typeof item.nameSize  === 'number' ? item.nameSize  : 4.5;
    const l2Start = typeof item.priceSize === 'number' ? item.priceSize : 3.5;
    const line2   = [varia, price].filter(Boolean).join(' · ');
    const nPt  = name  ? fitPt(name,  '400', nStart,  2.8, twMm, scale) : 0; // Typography A
    const l2Pt = line2 ? fitPt(line2, '400', l2Start, 2.3, twMm, scale) : 0;
    const l3Pt = 2.5;
    const nLH  = name  ? lhMm(nPt)  : 0;
    const l2LH = line2 ? lhMm(l2Pt) : 0;
    const l3LH = code  ? lhMm(l3Pt) : 0;
    const totalTH = nLH + (l2LH ? 0.6 + l2LH : 0) + (l3LH ? 0.5 + l3LH : 0);
    const top = px(TB) + Math.max(0, (px(usableH) - px(totalTH)) / 2);

    return (
      <div style={box}>
        <div style={{ position: 'absolute', left: px(SIDE), top: qrTop }}>
          <QRCodeSVG value={qrValue} size={Math.max(6, Math.round(px(qrMm)))} level="M" includeMargin={false} />
        </div>
        <div style={{ position: 'absolute', left: px(txMm), top, width: px(twMm), overflow: 'hidden' }}>
          {name  && <div style={{ fontWeight: 400, fontSize: ptPx(nPt),  lineHeight: px(nLH) +'px', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', color:'#111' }}>{name}</div>}
          {line2 && <div style={{ fontSize: ptPx(l2Pt), lineHeight: px(l2LH)+'px', whiteSpace:'nowrap', color:'#333', marginTop: px(0.6) }}>{varia}{varia && price ? ' · ' : ''}{price && <strong>{price}</strong>}</div>}
          {code  && <div style={{ fontSize: ptPx(l3Pt), lineHeight: px(l3LH)+'px', whiteSpace:'nowrap', color:'#aaa', marginTop: px(0.5) }}>{code}</div>}
        </div>
      </div>
    );
  }

  // ── AUTO-LAYOUT VERTICAL (>25mm) ──────────────────────────────────────────
  const VM = 1.5, VT = 1.5;
  const cwMm = width - VM * 2;
  const store = (item.storeName || '').trim();
  const nStart  = typeof item.nameSize === 'number' ? item.nameSize : 4.5;
  const vStart  = typeof item.varSize  === 'number' ? item.varSize  : 3.5;
  const nPt = name  ? fitPt(name,  '400', nStart, 2.8, cwMm, scale) : 0; // Typography A
  const vPt = varia ? fitPt(varia, '400', vStart, 2.5, cwMm, scale) : 0;
  const yAfterVar = VT + (store ? lhMm(3)+1.0 : 0) + (name ? lhMm(nPt)+1.0 : 0) + (varia ? lhMm(vPt)+1.2 : 0);
  const qrMax = Math.min(cwMm, Math.max(0, height - yAfterVar - VM - lhMm(5.5) - 1.0 - lhMm(2.5) - 0.6));
  const qrMm  = qrMax * clampQr(item.qrScale);
  const prSize = typeof item.priceSize === 'number' ? item.priceSize : 5.5;

  return (
    <div style={box}>
      <div style={{ position:'absolute', inset:0, display:'flex', flexDirection:'column', alignItems:'center', padding:px(VM), paddingTop:px(VT), boxSizing:'border-box', overflow:'hidden' }}>
        {store && <div style={{ fontSize:ptPx(3), lineHeight:px(lhMm(3))+'px', color:'#aaa', letterSpacing:'0.05em', flexShrink:0, marginBottom:px(1.0) }}>{store.toUpperCase()}</div>}
        {name  && <div style={{ fontWeight:400, fontSize:ptPx(nPt), lineHeight:px(lhMm(nPt))+'px', textAlign:'center', flexShrink:0, marginBottom:px(1.0), overflow:'hidden', maxWidth:'100%' }}>{name}</div>}
        {varia && <div style={{ fontSize:ptPx(vPt), lineHeight:px(lhMm(vPt))+'px', color:'#555', flexShrink:0, marginBottom:px(1.2), textAlign:'center' }}>{varia}</div>}
        {qrMm >= 5 && <div style={{ flexShrink:0, marginTop:px(0.6), marginBottom:px(1.5) }}><QRCodeSVG value={qrValue} size={Math.round(Math.min(px(cwMm), px(qrMm)))} level="M" includeMargin={false} /></div>}
        {price && <div style={{ fontWeight:700, fontSize:ptPx(prSize), lineHeight:px(lhMm(prSize))+'px', flexShrink:0, marginBottom:px(0.8) }}>{price}</div>}
        {code  && <div style={{ fontSize:ptPx(2.5), lineHeight:px(lhMm(2.5))+'px', color:'#aaa', flexShrink:0 }}>{code}</div>}
      </div>
    </div>
  );
}
